/**
 * 플레이리스트 상세 페이지 액션 훅
 *
 * 플레이리스트 관련 모든 mutation을 통합 관리합니다.
 * - 삭제, 생성, 재생성
 * - 트랙 추가
 * - 유튜브 메타데이터 생성
 * - 비디오 생성
 * - 오디오 내보내기
 */

'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { invalidatePlaylistList, invalidatePlaylist, invalidatePlaylistWithTracks } from '@/lib/api/cacheUtils';
import {
  usePlaylistDestroy,
  usePlaylistGenerate,
  usePlaylistRegenerate,
  usePlaylistAddTracks,
  usePlaylistGenerateYoutubeMetadata,
  usePlaylistGenerateVideo,
  usePlaylistExportAudio,
  usePlaylistToggleCompleted,
  usePlaylistConvertToWav,
  usePlaylistWavConversionProgress,
  usePlaylistRetrieve,
  usePlaylistRefreshDurations,
  usePlaylistSplitAllSecondVersions,
} from '@/lib/api/orval';
import { customInstance } from '@/lib/api/orval/custom-instance';
import {
  usePlaylistGenerateMetadata,
  usePlaylistStartMusicGeneration,
} from '@/lib/api/orval/endpoints/playlists/playlists';
import {
  EXPORT_POLLING_INTERVAL_MS,
  EXPORT_POLLING_TIMEOUT_MS,
  WAV_POLLING_INTERVAL_MS,
} from '@/lib/constants';
import { useToast } from '@/lib/hooks/useToast';
import { createMutationErrorHandler } from '@/lib/utils/errorUtils';

// ============================================================================
// Types
// ============================================================================

export interface UsePlaylistActionsOptions {
  playlistId: string;
  onDeleteSuccess?: () => void;
}

/**
 * 오디오 내보내기 응답 타입
 * API 응답 데이터를 타입 안전하게 처리하기 위한 인터페이스
 */
interface ExportAudioResponse {
  trackCount?: number;
  status?: string;
  message?: string;
}

/**
 * WAV 변환 진행 상황 응답 타입
 */
interface WavConversionProgressResponse {
  total?: number;
  completed?: number;
  progressPercent?: number;
  isComplete?: boolean;
}

/**
 * 재생시간 새로고침 응답 타입
 */
interface RefreshDurationsResponse {
  updated?: number;
  failed?: number;
  message?: string;
}

/**
 * 오디오 내보내기 응답 타입 가드
 */
function isExportAudioResponse(data: unknown): data is ExportAudioResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    ('trackCount' in data || 'status' in data || 'message' in data)
  );
}

/**
 * WAV 변환 진행 상황 응답 타입 가드
 */
function isWavConversionProgress(data: unknown): data is WavConversionProgressResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    ('total' in data || 'progressPercent' in data || 'isComplete' in data)
  );
}

/**
 * 재생시간 새로고침 응답 타입 가드
 */
function isRefreshDurationsResponse(data: unknown): data is RefreshDurationsResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    ('updated' in data || 'message' in data)
  );
}

// ============================================================================
// Hook
// ============================================================================

export function usePlaylistActions({ playlistId, onDeleteSuccess }: UsePlaylistActionsOptions) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();

  // 오디오 내보내기 폴링 상태 (TanStack Query refetchInterval 사용)
  const [isPollingExport, setIsPollingExport] = useState(false);
  const exportPollingStartRef = useRef<number>(0);

  // WAV 변환 폴링 상태
  const [isPollingWavConversion, setIsPollingWavConversion] = useState(false);

  // 플레이리스트 삭제
  const deletePlaylist = usePlaylistDestroy({
    mutation: {
      onSuccess: () => {
        invalidatePlaylistList(queryClient);
        toastSuccess('플레이리스트가 삭제되었습니다');
        onDeleteSuccess?.();
        router.push('/playlists');
      },
      onError: createMutationErrorHandler(toastError, '삭제 실패'),
    },
  });

  // 플레이리스트 생성 시작
  const generatePlaylist = usePlaylistGenerate({
    mutation: {
      onSuccess: () => {
        invalidatePlaylist(queryClient, playlistId);
        toastSuccess('플레이리스트 생성을 시작했습니다');
        router.push(`/playlists/${playlistId}`);
      },
      onError: createMutationErrorHandler(toastError, '생성 실패'),
    },
  });

  // 플레이리스트 재생성
  const regeneratePlaylist = usePlaylistRegenerate({
    mutation: {
      onSuccess: () => {
        invalidatePlaylistWithTracks(queryClient, playlistId);
        toastSuccess('플레이리스트 재생성을 시작했습니다');
        router.push(`/playlists/${playlistId}`);
      },
      onError: createMutationErrorHandler(toastError, '재생성 실패'),
    },
  });

  // 트랙 추가 (음악 생성 없이 트랙만 추가)
  const addTracks = usePlaylistAddTracks({
    mutation: {
      onSuccess: (data: { newTracks?: number; totalTracks?: number; trackIds?: string[] } | void) => {
        invalidatePlaylistWithTracks(queryClient, playlistId);
        const count = data && typeof data === 'object' && 'newTracks' in data ? data.newTracks : 1;
        const isReference = data && typeof data === 'object' && 'trackIds' in data && data.trackIds;
        if (isReference) {
          toastSuccess(`참조곡 ${count}곡 기반 메타데이터 생성이 시작되었습니다.`);
        } else {
          toastSuccess(`${count}개 트랙이 추가되었습니다. 프롬프트를 수정한 후 음악 생성을 시작하세요.`);
        }
      },
      onError: createMutationErrorHandler(toastError, '트랙 추가 실패'),
    },
  });

  // 유튜브 메타데이터 생성
  const generateYoutubeMetadata = usePlaylistGenerateYoutubeMetadata({
    mutation: {
      onSuccess: () => {
        invalidatePlaylist(queryClient, playlistId);
        toastSuccess('유튜브 메타데이터가 생성되었습니다');
      },
      onError: createMutationErrorHandler(toastError, '생성 실패'),
    },
  });

  // 비디오 생성
  const generatePlaylistVideo = usePlaylistGenerateVideo({
    mutation: {
      onSuccess: () => {
        invalidatePlaylist(queryClient, playlistId);
        toastSuccess('비디오 생성을 시작했습니다');
        router.push(`/playlists/${playlistId}`);
      },
      onError: createMutationErrorHandler(toastError, '비디오 생성 실패'),
    },
  });

  // 오디오 내보내기 폴링 (TanStack Query refetchInterval 사용)
  const exportPollingQuery = usePlaylistRetrieve(playlistId, {
    query: {
      enabled: isPollingExport,
      refetchInterval: (query) => {
        if (!isPollingExport) {
          return false;
        }
        // 타임아웃 체크
        if (Date.now() - exportPollingStartRef.current > EXPORT_POLLING_TIMEOUT_MS) {
          return false;
        }
        // exportedAudioUrl이 있으면 폴링 중단
        const data = query.state.data as { exportedAudioUrl?: string | null } | undefined;
        if (data?.exportedAudioUrl) {
          return false;
        }
        return EXPORT_POLLING_INTERVAL_MS;
      },
    },
  });

  // 내보내기 완료 또는 타임아웃 감지
  useEffect(() => {
    if (!isPollingExport) {
      return;
    }

    const data = exportPollingQuery.data as { exportedAudioUrl?: string | null } | undefined;
    if (data?.exportedAudioUrl) {
      setIsPollingExport(false);
      toastSuccess('오디오 내보내기가 완료되었습니다! 다운로드 버튼을 클릭하세요.');
      return;
    }

    // 타임아웃 체크
    if (exportPollingStartRef.current > 0 &&
        Date.now() - exportPollingStartRef.current > EXPORT_POLLING_TIMEOUT_MS) {
      setIsPollingExport(false);
      toastError('오디오 내보내기 시간이 초과되었습니다. 페이지를 새로고침해주세요.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportPollingQuery.data, isPollingExport]);

  // 오디오 내보내기
  const exportPlaylistAudio = usePlaylistExportAudio({
    mutation: {
      onSuccess: (data) => {
        // 타입 가드를 사용하여 안전하게 trackCount 추출
        const trackCount = isExportAudioResponse(data) ? (data.trackCount ?? 0) : 0;
        toastSuccess(`오디오 내보내기가 시작되었습니다 (${trackCount}개 트랙)`);
        // TanStack Query refetchInterval 기반 폴링 시작
        exportPollingStartRef.current = Date.now();
        setIsPollingExport(true);
      },
      onError: createMutationErrorHandler(toastError, '오디오 내보내기 실패'),
    },
  });

  // 메타데이터 생성 (배치) - 새 워크플로우: pending → generating → metadata_ready
  const generateMetadata = usePlaylistGenerateMetadata({
    mutation: {
      onSuccess: () => {
        invalidatePlaylistWithTracks(queryClient, playlistId);
        toastSuccess('메타데이터 생성을 시작했습니다. 완료 후 검토해주세요.');
        router.push(`/playlists/${playlistId}`);
      },
      onError: createMutationErrorHandler(toastError, '메타데이터 생성 실패'),
    },
  });

  // 음악 생성 시작 - 새 워크플로우: metadata_ready → music_generating → completed
  const startMusicGeneration = usePlaylistStartMusicGeneration({
    mutation: {
      onSuccess: () => {
        invalidatePlaylistWithTracks(queryClient, playlistId);
        toastSuccess('음악 생성을 시작했습니다');
        router.push(`/playlists/${playlistId}`);
      },
      onError: createMutationErrorHandler(toastError, '음악 생성 시작 실패'),
    },
  });

  // YouTube 비디오 내보내기 (JSON Content-Type으로 직접 전송)
  const exportYoutubeVideo = useMutation({
    mutationFn: (options: Record<string, unknown>) =>
      customInstance<void>({
        url: `/api/playlists/${playlistId}/export-youtube-video/`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: options,
      }),
    onSuccess: () => {
      toastSuccess('YouTube 비디오 내보내기가 시작되었습니다');
      invalidatePlaylist(queryClient, playlistId);
    },
    onError: createMutationErrorHandler(toastError, 'YouTube 비디오 내보내기 실패'),
  });

  // 플레이리스트 완료 상태 토글
  const toggleCompleted = usePlaylistToggleCompleted({
    mutation: {
      onSuccess: (data: { status?: string; previousStatus?: string; message?: string }) => {
        invalidatePlaylist(queryClient, playlistId);
        invalidatePlaylistList(queryClient);
        // 응답의 message 필드를 사용하여 토스트 표시
        const message = data.message || (data.status === 'completed' ? '완료 상태로 변경되었습니다' : '완료 상태가 해제되었습니다');
        toastSuccess(message);
      },
      onError: createMutationErrorHandler(toastError, '상태 변경 실패'),
    },
  });

  // WAV 변환 진행 상황 폴링 쿼리 (폴링 중일 때만 활성화)
  const wavConversionProgressQuery = usePlaylistWavConversionProgress(playlistId, {
    query: {
      enabled: isPollingWavConversion,
      refetchInterval: isPollingWavConversion ? WAV_POLLING_INTERVAL_MS : false,
    },
  });

  // WAV 변환 진행 상황 데이터 파싱
  // API 응답이 void로 타입되어 있지만 실제로는 객체를 반환함
  const wavConversionProgress = useMemo(() => {
    // 폴링이 활성화되지 않았거나 데이터가 없으면 undefined 반환
    if (!isPollingWavConversion) {
      return undefined;
    }

    // 타입 가드를 사용하여 안전하게 파싱
    const rawData = wavConversionProgressQuery.data;
    if (!isWavConversionProgress(rawData)) {
      return undefined;
    }

    return {
      total: rawData.total || 0,
      completed: rawData.completed || 0,
      progressPercent: rawData.progressPercent || 0,
      isComplete: rawData.isComplete || false,
    };
  }, [wavConversionProgressQuery.data, isPollingWavConversion]);

  // WAV 변환 완료 감지 시 폴링 중단
  useEffect(() => {
    if (wavConversionProgress?.isComplete && isPollingWavConversion) {
      setIsPollingWavConversion(false);
      toastSuccess('WAV 변환이 완료되었습니다! 고품질 오디오를 내보낼 수 있습니다.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wavConversionProgress?.isComplete, isPollingWavConversion]);

  // WAV 변환 시작
  const convertToWav = usePlaylistConvertToWav({
    mutation: {
      onSuccess: () => {
        invalidatePlaylist(queryClient, playlistId);
        toastSuccess('WAV 변환을 시작했습니다. 잠시 기다려주세요...');
        setIsPollingWavConversion(true);
      },
      onError: createMutationErrorHandler(toastError, 'WAV 변환 시작 실패'),
    },
  });

  // 트랙 버전 재생시간 새로고침
  const refreshDurations = usePlaylistRefreshDurations({
    mutation: {
      onSuccess: (data) => {
        invalidatePlaylistWithTracks(queryClient, playlistId);
        const message = isRefreshDurationsResponse(data) ? data.message : undefined;
        toastSuccess(message || '재생시간이 업데이트되었습니다');
      },
      onError: createMutationErrorHandler(toastError, '재생시간 새로고침 실패'),
    },
  });

  // 모든 트랙의 2번째 버전 일괄 분리
  const splitAllSecondVersions = usePlaylistSplitAllSecondVersions({
    mutation: {
      onSuccess: (data) => {
        invalidatePlaylistWithTracks(queryClient, playlistId);
        const message =
          typeof data === 'object' && data !== null && 'message' in data
            ? String((data as { message: unknown }).message)
            : '2번째 버전 분리가 완료되었습니다';
        toastSuccess(message);
      },
      onError: createMutationErrorHandler(toastError, '2번째 버전 분리 실패'),
    },
  });

  // 핸들러 함수들
  const handleDelete = async () => {
    await deletePlaylist.mutateAsync({ id: playlistId });
  };

  const handleGenerate = async (numTracks?: number) => {
    await generatePlaylist.mutateAsync({
      id: playlistId,
      data: numTracks ? { numTracks } : {},
    });
  };

  const handleRegenerate = async (numTracks?: number) => {
    await regeneratePlaylist.mutateAsync({
      id: playlistId,
      data: numTracks ? { numTracks } : {},
    });
  };

  const handleAddTracks = async (count: number) => {
    if (isNaN(count) || count < 1) {
      toastError('1개 이상의 트랙을 추가해야 합니다');
      return false;
    }
    await addTracks.mutateAsync({
      id: playlistId,
      data: { numTracks: count },
    });
    return true;
  };

  const handleAddReferenceTracks = async (
    songs: Array<{ title: string; artist: string; vocal: string }>
  ) => {
    if (!songs || songs.length < 1) {
      toastError('참고곡을 1곡 이상 입력해주세요');
      return false;
    }
    await addTracks.mutateAsync({
      id: playlistId,
      data: { referenceSongs: songs },
    });
    return true;
  };

  const handleGenerateYoutubeMetadata = async () => {
    await generateYoutubeMetadata.mutateAsync({ id: playlistId });
  };

  const handleGenerateVideo = async () => {
    await generatePlaylistVideo.mutateAsync({ id: playlistId });
  };

  const handleExportAudio = async (options?: { format?: 'mp3' | 'wav'; bitrate?: string }) => {
    await exportPlaylistAudio.mutateAsync({
      id: playlistId,
      data: options ?? {},
    });
  };

  // YouTube 비디오 내보내기
  const handleExportYoutubeVideo = async (options: Record<string, unknown>) => {
    await exportYoutubeVideo.mutateAsync(options);
  };

  // 메타데이터 생성 (새 워크플로우)
  const handleGenerateMetadata = async (numTracks?: number) => {
    await generateMetadata.mutateAsync({
      id: playlistId,
      data: numTracks !== undefined && numTracks >= 1 && numTracks <= 50
        ? { numTracks }
        : {},
    });
  };

  // 음악 생성 시작 (새 워크플로우)
  const handleStartMusicGeneration = async () => {
    await startMusicGeneration.mutateAsync({ id: playlistId });
  };

  // 완료 상태 토글
  const handleToggleCompleted = async () => {
    await toggleCompleted.mutateAsync({ id: playlistId });
  };

  // WAV 변환 시작
  const handleConvertToWav = async () => {
    await convertToWav.mutateAsync({ id: playlistId });
  };

  // 재생시간 새로고침
  const handleRefreshDurations = async () => {
    await refreshDurations.mutateAsync({ id: playlistId });
  };

  // 모든 트랙의 2번째 버전 일괄 분리
  const handleSplitAllSecondVersions = async () => {
    await splitAllSecondVersions.mutateAsync({ id: playlistId });
  };

  return {
    // 로딩 상태
    isDeleting: deletePlaylist.isPending,
    isGenerating: generatePlaylist.isPending || generateMetadata.isPending,
    isRegenerating: regeneratePlaylist.isPending,
    isAddingTracks: addTracks.isPending,
    isGeneratingMetadata: generateYoutubeMetadata.isPending,
    isGeneratingVideo: generatePlaylistVideo.isPending,
    isExportingAudio: exportPlaylistAudio.isPending || isPollingExport,
    isExportingYoutubeVideo: exportYoutubeVideo.isPending,
    isStartingMusicGeneration: startMusicGeneration.isPending,
    isTogglingStatus: toggleCompleted.isPending,
    isConvertingToWav: convertToWav.isPending || isPollingWavConversion,
    isRefreshingDurations: refreshDurations.isPending,
    isSplittingAllSecondVersions: splitAllSecondVersions.isPending,

    // WAV 변환 진행 상황
    wavConversionProgress,

    // 핸들러
    handleDelete,
    handleGenerate,
    handleRegenerate,
    handleAddTracks,
    handleAddReferenceTracks,
    handleGenerateYoutubeMetadata,
    handleGenerateVideo,
    handleExportAudio,
    handleExportYoutubeVideo,
    handleGenerateMetadata,
    handleStartMusicGeneration,
    handleToggleCompleted,
    handleConvertToWav,
    handleRefreshDurations,
    handleSplitAllSecondVersions,
  };
}

export type UsePlaylistActionsReturn = ReturnType<typeof usePlaylistActions>;
